import { useEffect, useState, useCallback } from 'react';
import {
  AppDatePicker,
  AppFileUploader,
  AppInput,
  AppMultiSelect,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
  AppDeleteModal,
} from '@/layout/AppComponents';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import ReactDOMServer from 'react-dom/server';
import { axiosPath } from '../../../../../../../routing/helpers/router';
import * as currentPageAction from '../../../../../../../store/actions/currentPageAction';
import Translator from '../../../../../../components/helpers/Translator';
import colors from '../../../../../../../constants/colors';
import { toFormData } from '../../../../../../../minions/forms';
import NotificationAlert from '../../../../../../../layout/components/helpers/notificationAlert';
import AppTextEditor from '../../../../../../components/form/Texts/AppTextEditor';
import errorHandler from '@/layout/components/error/ErrorHandler';
import _ from 'lodash';

const CorrectiveActionsEditTaskUpdate = (props) => {
  const {
    plantObligationID,
    onCreated,
    modulePermissions,
    selectedTask,
    selectedTaskUpdate,
    caUserBridgeID,
  } = props;
  const { moduleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const permissions = useSelector(
    (state) => state.module[moduleBridgeHex].permissions
  );
  const user = useSelector((state) => state.auth?.user);
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge
  );
  const [typeOptions, setTypeOptions] = useState();
  const [responsibleOptions, setResponsibleOptions] = useState();
  const [showTaskUpdateDeleteModal, setShowTaskUpdateDeleteModal] =
    useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // < GET LISTS
  const getEditLists = useCallback(async () => {
    await axios
      .post(
        axiosPath(
          "api_r_user_module_legalCompliance_correctiveActions_taskUpdate_getEditLists"
        ),
        {
          moduleBridgeHex,
          plantHex,
          plantObligationID,
        }
      )
      .then((r) => {
        setTypeOptions(r.data.typeOptions);
        setResponsibleOptions(r.data.responsibleOptions);
      });
  }, []);
  useEffect(() => {
    getEditLists();
  }, []);
  // > GET LISTS

  // < NEW TASK UPDATE
  const handleEditTaskUpdate = async (values) => {
    try {
      const response = await axios.post(
        axiosPath(
          "api_r_user_module_legalCompliance_correctiveActions_taskUpdate_edit"
        ),
        toFormData({
          ...values,
          ...{
            moduleBridgeHex,
            plantHex,
            plantObligationID,
            taskID: selectedTask.id,
            taskUpdateID: selectedTaskUpdate.id,
          },
        }),
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      dispatch(
        currentPageAction.change(
          "correctiveActionsTabs",
          { [plantObligationID]: response.data },
          true
        )
      );
      onCreated();
    } catch (err) {
      dispatch(errorHandler(err));
    }
  };
  // > NEW TASK UPDATE

  // > REMOVE TASK
  const handleRemoveTaskUpdate = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      const response = await axios.post(
        axiosPath(
          "api_r_user_module_legalCompliance_correctiveActions_taskUpdate_remove"
        ),
        {
          moduleBridgeHex,
          plantHex,
          plantObligationID,
          taskUpdateID: selectedTaskUpdate.id,
        }
      );
      dispatch(
        currentPageAction.change(
          'correctiveActionsTabs',
          { [plantObligationID]: response.data },
          true
        )
      );
      setShowTaskUpdateDeleteModal(false);
      setIsDeleting(false);
      onCreated();
    } catch (err) {
      setShowTaskUpdateDeleteModal(false);
      setIsDeleting(false);
      NotificationAlert({
        title: ReactDOMServer.renderToString(
          <Translator file="user" name="user.error" />
        ),
        message: ReactDOMServer.renderToString(
          <Translator file="user" name="user.error" />
        ),
        type: 'danger',
        position: 'top-center',
        duration: 0,
      });
    }
  };
  // < REMOVE TASK

  const validationSchema = Yup.object().shape({
    date: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="module-legalCompliance" name="must.select.date" />)
    ),
    type: Yup.mixed().required(
      ReactDOMServer.renderToString(<Translator file="module-legalCompliance" name="must.select.origin" />)
    ),
    comment: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)
    ),
  });

  return (
    <div style={{ borderTop: `1px solid ${colors.grey300}` }} className="pt-2">
      <Formik
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize
        initialValues={{
          date: selectedTaskUpdate.date,
          limitDate: selectedTaskUpdate.limitDate,
          type: selectedTaskUpdate.type.id,
          file: null,
          employees: _.map(selectedTaskUpdate.responsibles, 'id'),
          owner: permissions.gcl?.module?.submodules?.correctiveActions
            ?.supervisionSystem
            ? selectedTaskUpdate.controlUser?.userBridgeID
            : null,
          supervisor: permissions.gcl?.module?.submodules?.correctiveActions
            ?.supervisionSystem
            ? selectedTaskUpdate?.controlSupervisor?.userBridgeID
            : null,
          controlUserStatus: permissions.gcl?.module?.submodules
            ?.correctiveActions?.supervisionSystem
            ? selectedTaskUpdate.controlUserStatus
            : null,
          comment: selectedTaskUpdate.description,
          removeFile: false,
        }}
        onSubmit={handleEditTaskUpdate}
      >
        {({ setFieldValue, handleSubmit, values, isSubmitting, errors }) => (
          <>
            <div className="row-no-margin">
              <div className="row">
                <div className="col-lg-12">
                  <h3>
                    {ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="edit.action.code"
                        variables={{ code: selectedTask.code }}
                      />
                    )}
                  </h3>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-6">
                  <AppDatePicker
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="creation.date"
                      />
                    )}
                    value={values.date}
                    onChange={(opt) => setFieldValue('date', opt)}
                    placeholder="dd/mm/yyyy"
                    error={errors.date}
                  />
                </div>
                {permissions.gcl?.module?.submodules?.correctiveActions
                  ?.taskUpdateClosedSystem && (
                  <div className="col-lg-6">
                    <AppDatePicker
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="module.lc.limit.date"
                        />
                      )}
                      value={values.limitDate}
                      onChange={(opt) => setFieldValue('limitDate', opt)}
                      placeholder="dd/mm/yyyy"
                      error={errors.limitDate}
                    />
                  </div>
                )}

                <div className="col-lg-6">
                  {typeOptions && (
                    <AppSingleSelect
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-correctiveActions"
                          name="module.ca.task-type-2"
                        />
                      )}
                      icon={<AppRemixIcon icon="focus" />}
                      value={values.type}
                      options={typeOptions}
                      onChange={(v) => setFieldValue('type', v)}
                      error={errors.type}
                    />
                  )}
                </div>

                {selectedTaskUpdate.file && !values.removeFile ? (
                  <div className="col-lg-6">
                    <div
                      onClick={() => setFieldValue('removeFile', true)}
                      placeholder={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="click.delete.current.file"
                        />
                      )}
                    >
                      <AppInput
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="user"
                            name="user.obligation.evidence.file"
                          />
                        )}
                        value={selectedTaskUpdate.file.url}
                        icon={
                          <AppRemixIcon
                            icon="delete-bin"
                            color={colors.danger}
                          />
                        }
                        isReadOnly
                      />
                    </div>
                  </div>
                ) : (
                  <div className="col-lg-6 pt-2">
                    <AppFileUploader
                      placeholder={
                        !values.file
                          ? ReactDOMServer.renderToString(
                              <Translator file="general" name="select.a.file" />
                            )
                          : values.file[0].name
                      }
                      onChange={(opt) => setFieldValue('file', opt)}
                      btnText={<Translator file="general" name="search..." />}
                    />
                  </div>
                )}
              </div>
              {permissions.gcl?.module?.submodules?.correctiveActions
                ?.supervisionSystem ? (
                <div className="row">
                  <div className="col-lg-4">
                    {userBridge?.permissions?.level === 0 ? (
                      responsibleOptions ? (
                        <AppSingleSelect
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="owner"
                            />
                          )}
                          icon={<AppRemixIcon icon="user" />}
                          value={values.owner}
                          options={responsibleOptions}
                          onChange={(v) => setFieldValue('owner', v)}
                          isLoading={!responsibleOptions}
                        />
                      ) : (
                        <></>
                      )
                    ) : (
                      <AppInput
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-legalCompliance"
                            name="owner"
                          />
                        )}
                        value={selectedTaskUpdate?.controlUser?.name}
                        isReadOnly
                      />
                    )}
                  </div>
                  <div className="col-lg-4">
                    {responsibleOptions && (
                      <AppSingleSelect
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-correctiveActions"
                            name="supervisor"
                          />
                        )}
                        icon={<AppRemixIcon icon="user" />}
                        value={values.supervisor}
                        options={responsibleOptions}
                        onChange={(v) => setFieldValue('supervisor', v)}
                        isLoading={!responsibleOptions}
                      />
                    )}
                  </div>
                  <div className="col-lg-4">
                    {caUserBridgeID ===
                      selectedTaskUpdate?.controlUser?.userBridgeID && (
                      <AppSingleSelect
                        title={ReactDOMServer.renderToString(
                          <Translator file="user" name="user.state" />
                        )}
                        icon={<AppRemixIcon icon="user" />}
                        value={values.controlUserStatus}
                        options={[
                          {
                            label: ReactDOMServer.renderToString(
                              <Translator
                                file="module-correctiveActions"
                                name="not.accepted"
                              />
                            ),
                            value: 0,
                          },
                          {
                            label: ReactDOMServer.renderToString(
                              <Translator
                                file="module-correctiveActions"
                                name="accepted"
                              />
                            ),
                            value: 1,
                          },
                        ]}
                        onChange={(v) => setFieldValue('controlUserStatus', v)}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <div className="row">
                  <div className="col-lg-12">
                    <AppMultiSelect
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="responsibles"
                        />
                      )}
                      icon={<AppRemixIcon icon="user" />}
                      value={values.employees}
                      options={responsibleOptions}
                      onChange={(v) => setFieldValue('employees', v)}
                      isLoading={!responsibleOptions}
                      hasSearchBox
                      hasSelectOptions
                    />
                  </div>
                </div>
              )}
              <div className="row">
                <div className="col-lg-12">
                  <AppTextArea
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="comment"
                      />
                    )}
                    height={
                      modulePermissions?.caAutoCalc &&
                      modulePermissions.caAutoCalc == 1
                        ? '140px'
                        : '190px'
                    }
                    isReadOnly
                    value={
                      <AppTextEditor
                        value={values.comment}
                        onChange={(opt) => setFieldValue('comment', opt)}
                        toolbar={{
                          toolbar: [['bold', 'italic']],
                        }}
                        height={117}
                      />
                    }
                    error={errors.comment}
                  />
                </div>
              </div>
              {/* {modulePermissions?.caAutoCalc && modulePermissions.caAutoCalc == 1 && ( */}
              {/*  <div className="row"> */}
              {/*    <div className="col-lg-6 pt-2"> */}
              {/*      <AppInputNumber */}
              {/*        title="implantation" */}
              {/*        value={values.implantation} */}
              {/*        icon={<AppRemixIcon icon="calendar" />} */}
              {/*        onChange={(v) => setFieldValue('implantation', v)} */}
              {/*        decimals={2} */}
              {/*      /> */}
              {/*    </div> */}
              {/*    <div className="col-lg-6 pt-2"> */}
              {/*      <AppInputNumber */}
              {/*        title="efficacy" */}
              {/*        value={values.efficacy} */}
              {/*        icon={<AppRemixIcon icon="calendar" />} */}
              {/*        onChange={(v) => setFieldValue('efficacy', v)} */}
              {/*        decimals={2} */}
              {/*      /> */}
              {/*    </div> */}
              {/*  </div> */}
              {/* )} */}

              <div className="row">
                <div className="col-lg-12">
                  <span
                    className="btn btn-outline-danger"
                    onClick={() => setShowTaskUpdateDeleteModal(true)}
                  >
                    <Translator file="user" name="user.remove" />
                  </span>
                  <AppDeleteModal
                    show={showTaskUpdateDeleteModal}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="delete.task.update"
                      />
                    )}
                    onConfirm={() => handleRemoveTaskUpdate()}
                    onClose={() => setShowTaskUpdateDeleteModal(false)}
                  />
                  <div className="float-right">
                    <span
                      className="btn btn-outline-light mr-2"
                      onClick={() => onCreated()}
                    >
                      <Translator file="user" name="user.cancel" />
                    </span>
                    <span
                      className="btn btn-outline-info"
                      onClick={handleSubmit}
                    >
                      <Translator file="general" name="general.update" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </Formik>
    </div>
  );
};
export default CorrectiveActionsEditTaskUpdate;
